import * as React from "react";
import {
  cancelDDQ,
  streamDDQAnswer,
  type DDQAnsweredQuestion,
  type DDQStructuredPayload,
  type DDQUploadResponse,
} from "@/lib/api";

/**
 * useDdqRun — the HEADLESS DDQ run orchestration.
 *
 * Encapsulates the upload → review → progress phase machine and the answer
 * stream that previously lived inline in `ChatPage` (see git blame on
 * ChatPage's `startDDQRun` / `cancelDDQRun` / `onDDQUploaded`). It owns ONLY
 * the DDQ-run-specific state; anything the host surface renders (the chat
 * message list, the source panel, the active conversation id, error banners)
 * stays in the host and is fed via the host callbacks below. This lets BOTH
 * `ChatPage` (the standalone / embedded chat) and the suite `DDQView` drive the
 * exact same run without either one duplicating the SSE pipeline.
 *
 * CRITICAL: the streaming pipeline, the polling fallback, and the question
 * de-dupe all live in `api.ts` (`streamDDQAnswer`). This hook calls that ONE
 * function and copies none of it. Cancellation is dual — `cancelRun` calls
 * `cancelDDQ` (the server-side stop) AND aborts the fetch — because the worker
 * keeps generating in a detached background IIFE after a client disconnect, so
 * merely aborting the reader would not stop server-side generation (or the
 * OpenRouter token burn / MAX_DDQS_PER_DAY quota).
 */

export type DdqRunPhase = "idle" | "upload" | "review" | "progress";

/** Payload handed to `onDone` — everything the host needs to stamp its own
 *  message + source-panel state, computed once inside the hook so the host
 *  never re-derives ordering. `questions` is already in the LP's original DDQ
 *  section+question order (the SSE arrival order is re-sorted here). */
export type DdqRunDonePayload = {
  messageId: string;
  submissionId: string;
  displayName: string;
  fundId: string;
  cancelled: boolean;
  /** DDQ-response payload, questions in parsed order — stamp straight into a
   *  message.structured_payload. */
  payload: DDQStructuredPayload;
  /** SSE-arrival-ordered results (unsorted) — the raw accumulator, kept for a
   *  host that wants the exact stream order (ChatPage uses `payload.questions`,
   *  which IS ordered). */
  results: DDQAnsweredQuestion[];
};

export type UseDdqRunOptions = {
  /** Fired when the run's conversation id lands (SSE `conversation` event),
   *  carrying the total question count + the DDQ display name so the host can
   *  render an optimistic "please answer …" user message. */
  onConversationId?: (
    conversationId: string,
    submissionId: string,
    total: number,
    displayName: string,
  ) => void;
  /** Fired once the run reaches its terminal `done` event, with everything the
   *  host needs to persist the answer message + aggregate the source panel. */
  onDone?: (payload: DdqRunDonePayload) => void;
  /** Fired on any run error (SSE `error` event, non-abort stream throw, or a
   *  polling-fallback timeout). Aborts are swallowed (a cancel is not an error). */
  onError?: (message: string) => void;
};

export type UseDdqRunReturn = {
  phase: DdqRunPhase;
  upload: DDQUploadResponse | null;
  results: DDQAnsweredQuestion[];
  retrievalDone: boolean;
  total: number;
  cancelling: boolean;
  /** Move to the upload phase, clearing any prior run state. */
  openUpload: () => void;
  /** Called by the UploadDialog's `onUploaded` — stashes the parsed upload and
   *  advances to the review phase. */
  onUploaded: (resp: DDQUploadResponse) => void;
  /** Kick off the answer stream for the selected question ids. */
  startRun: (selectedQuestionIds: string[]) => Promise<void>;
  /** Cancel an in-flight run: server stop (cancelDDQ) AND abort the fetch. */
  cancelRun: () => Promise<void>;
  /** Return to idle + clear all run state (upload/results/progress). */
  reset: () => void;
};

export function useDdqRun(options: UseDdqRunOptions = {}): UseDdqRunReturn {
  const { onConversationId, onDone, onError } = options;

  // ─── DDQ-run state (moved near-verbatim from ChatPage) ────────────────────
  //   phase drives which dialog/overlay the host shows:
  //     idle     — nothing DDQ in flight
  //     upload   — UploadDialog open
  //     review   — ReviewDialog open with parsed questions
  //     progress — generation in flight; host renders ProgressView
  const [phase, setPhase] = React.useState<DdqRunPhase>("idle");
  const [upload, setUpload] = React.useState<DDQUploadResponse | null>(null);
  const [results, setResults] = React.useState<DDQAnsweredQuestion[]>([]);
  const [retrievalDone, setRetrievalDone] = React.useState(false);
  const [total, setTotal] = React.useState(0);
  const [cancelling, setCancelling] = React.useState(false);
  const abortRef = React.useRef<AbortController | null>(null);

  // Mirror `upload` into a ref so `startRun`/`cancelRun` can stay dependency-
  // free (stable identities) yet always read the current upload — matching the
  // stable identities ChatPage relied on for its render behavior.
  const uploadRef = React.useRef<DDQUploadResponse | null>(upload);
  React.useEffect(() => {
    uploadRef.current = upload;
  }, [upload]);

  // Keep the host callbacks in refs so `startRun`/`cancelRun` stay stable and
  // the async closure always reads the latest handler without re-creating the
  // callbacks on every host re-render.
  const onConversationIdRef = React.useRef(onConversationId);
  const onDoneRef = React.useRef(onDone);
  const onErrorRef = React.useRef(onError);
  React.useEffect(() => {
    onConversationIdRef.current = onConversationId;
    onDoneRef.current = onDone;
    onErrorRef.current = onError;
  }, [onConversationId, onDone, onError]);

  const openUpload = React.useCallback(() => {
    setUpload(null);
    setResults([]);
    setRetrievalDone(false);
    setTotal(0);
    setPhase("upload");
  }, []);

  const onUploaded = React.useCallback((resp: DDQUploadResponse) => {
    setUpload(resp);
    setPhase("review");
  }, []);

  const reset = React.useCallback(() => {
    setPhase("idle");
    setUpload(null);
    setResults([]);
    setRetrievalDone(false);
    setTotal(0);
    setCancelling(false);
    abortRef.current = null;
  }, []);

  const startRun = React.useCallback(
    async (selected: string[]) => {
      // `upload` is state; read the latest via the setter-free closure guard.
      // (ChatPage guarded `if (!ddqUpload) return`; preserve that.)
      const activeUpload = uploadRef.current;
      if (!activeUpload) return;
      setPhase("progress");
      setResults([]);
      setRetrievalDone(false);
      setTotal(selected.length);

      const controller = new AbortController();
      abortRef.current = controller;

      // Local accumulator so onDone reads the up-to-date answer set without
      // relying on stale React state inside the closure.
      const accumulated: DDQAnsweredQuestion[] = [];

      try {
        await streamDDQAnswer(
          activeUpload.submission_id,
          selected,
          {
            onConversationId: (convId, subId, streamTotal) => {
              setTotal(streamTotal);
              onConversationIdRef.current?.(
                convId,
                subId,
                streamTotal,
                activeUpload.display_name,
              );
            },
            onRetrievalComplete: () => setRetrievalDone(true),
            onQuestionComplete: (q) => {
              accumulated.push(q);
              setResults([...accumulated]);
            },
            onDone: ({ message_id, cancelled }) => {
              // Sort the SSE-arrival-ordered `accumulated` array to match the
              // LP's original DDQ section + question order. The server already
              // re-orders its persisted copy, but the in-memory payload stamped
              // here would otherwise expose the parallel-generation arrival
              // order to the accordion grouping logic.
              const orderedById = new Map<string, number>();
              let pos = 0;
              for (const sec of activeUpload.parsed_questions.sections) {
                for (const q of sec.questions) {
                  orderedById.set(q.id, pos);
                  pos += 1;
                }
              }
              const ordered = [...accumulated].sort(
                (a, b) =>
                  (orderedById.get(a.question_id) ?? Number.MAX_SAFE_INTEGER) -
                  (orderedById.get(b.question_id) ?? Number.MAX_SAFE_INTEGER),
              );
              const payload: DDQStructuredPayload = {
                kind: "ddq_response",
                submission_id: activeUpload.submission_id,
                display_name: activeUpload.display_name,
                fund_id: activeUpload.fund_id,
                question_count: ordered.length,
                cancelled,
                questions: ordered,
              };
              setPhase("idle");
              abortRef.current = null;
              setCancelling(false);
              onDoneRef.current?.({
                messageId: message_id,
                submissionId: activeUpload.submission_id,
                displayName: activeUpload.display_name,
                fundId: activeUpload.fund_id,
                cancelled,
                payload,
                results: accumulated,
              });
            },
            onError: (message) => {
              setPhase("idle");
              abortRef.current = null;
              onErrorRef.current?.(message || "DDQ run failed");
            },
          },
          controller.signal,
        );
      } catch (err) {
        if ((err as Error)?.name === "AbortError") return;
        setPhase("idle");
        abortRef.current = null;
        onErrorRef.current?.(
          err instanceof Error ? err.message : "DDQ stream failed",
        );
      }
    },
    [],
  );

  const cancelRun = React.useCallback(async () => {
    const activeUpload = uploadRef.current;
    if (!activeUpload) return;
    setCancelling(true);
    try {
      // Server-side stop FIRST: the worker keeps generating in a detached
      // background IIFE after a client disconnect, so aborting the reader alone
      // would leave the run consuming OpenRouter tokens + the daily quota.
      await cancelDDQ(activeUpload.submission_id);
    } catch (err) {
      // Best-effort — still abort the client stream below.
      console.error("cancelDDQ failed", err);
    } finally {
      // Force the SSE stream closed and return to idle. Server-side the
      // generation loop sees the status flip on its next question and persists
      // a partial-DDQ message; the host can reopen the conversation to read what
      // was answered.
      abortRef.current?.abort();
      abortRef.current = null;
      setPhase("idle");
      setCancelling(false);
      setUpload(null);
    }
  }, []);

  return {
    phase,
    upload,
    results,
    retrievalDone,
    total,
    cancelling,
    openUpload,
    onUploaded,
    startRun,
    cancelRun,
    reset,
  };
}
